import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { DataFormService } from '@shared/components/data-form/data-form.service';
import { Question, QuestionFieldProps, QuestionsExtra } from '@shared/models/domain/custom-views.model';
import { SafePipe } from '@shared/pipes/safe.pipe';
import { WebsiteSettingsViewService } from '@shared/services/settings.service';

@Component({
  selector: 'formly-question',
  imports: [
    MatCheckbox,
    MatInput,
    MatFormField,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    SafePipe,
    MatRadioGroup,
    MatRadioButton,
],
  templateUrl: './question-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuestionFormComponent extends FieldType<FieldTypeConfig<QuestionFieldProps>> implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private websiteSettings = inject(WebsiteSettingsViewService);
  private formService = inject(DataFormService);
  private fb = inject(FormBuilder);

  questions: Question[] = [];
  description: string;
  questionsForm: FormGroup;

  constructor() {
    super();

    effect(() => {
        const invalidForm = this.formService.isInvalidQuestionForm();
        if (invalidForm) {
          this.markFormAsTouched();
        }
      });
  }

  ngOnInit(): void {
    this.websiteSettings.currentWebsiteSettings$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((settings) => {
        if (settings) { 
          try {
            const slugSetting = this.field?.props?.slug && settings[this.field.props.slug];
            if (slugSetting?.settingsValue) {
              const parsedSettings = JSON.parse(slugSetting.settingsValue) as QuestionsExtra;
              this.questions = parsedSettings?.questions || [];
            } else {
              this.questions = [];
            }
          } catch {
            this.questions = [];
          }
          
          const descriptionSlugSetting = this.field?.props?.descriptionSlug && settings[this.field.props.descriptionSlug];
          this.description = this.field?.props?.allowSignatureLabel && descriptionSlugSetting?.settingsValue;
          this.createFormControls();
        }
      });
  }

  /**
   * Marks all form controls as touched
   */
  markFormAsTouched(): void {
    Object.values(this.questionsForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  private createFormControls(): void {
    const group = {};

    this.questions?.forEach((question, index) => {
      group[`question_${index}`] = ['', question?.type === "Boolean" ? Validators.requiredTrue : Validators.required];
    });

    this.questionsForm = this.fb.group(group);

    this.questionsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((values: Record<string, string | boolean | number | number[]>) => {
        const allAnswered = Object.values(values).every(value => {
          if (!value) return false;
          if (Array.isArray(value) && value.length === 0) return false;
          return true;
        });

        if (allAnswered) {
          const formValue = this.questions.map((item, index) => {

            const answerValue = this.questionsForm?.controls[`question_${index}`]?.value as string | boolean | number | number[];
            let answerText: string | string[] | boolean;

            switch (item.type) {
              case 'Boolean':
                answerText = answerValue ? true : false;
                break;
              case 'Selection': {
                const selectedAnswer = item.answers.find(a => a.id === answerValue);
                answerText = selectedAnswer ? selectedAnswer.answer : '';
                break;
              }
              case 'MultipleSelection': {
                const selectedAnswers = item.answers.filter(a => (answerValue as number[]).includes(a.id));
                answerText = selectedAnswers.map(a => a.answer);
                break;
              }
              case 'YesNo':
                answerText = answerValue as string;
                break;
            }

            return {
              question: item.question,
              answer: answerText
            };
          });
          this.formControl.setValue(formValue);
          this.formControl.setErrors(null);
        } else {
          this.formControl.setErrors({ required: true });
        }
      });
  }
}
